import React from "react";
import { getWhtProfile } from "../../utils/whtProfile";

// Withholding tax profile (docs/WHT_IMPLEMENTATION.md §2), stored on
// AccountingClient.whtProfile.

export const TAXPAYER_SIZES = [
  { value: "micro", label: "Micro" },
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

const FLAGS = [
  {
    key: "isTopWithholdingAgent",
    label: "Top withholding agent",
    help: "Enables ATC WI/WC158 and WI/WC160.",
  },
  {
    key: "isEfps",
    label: "eFPS filer",
    help: "Uses the eFPS due dates.",
  },
  {
    key: "isLargeTaxpayer",
    label: "Large taxpayer",
    help: "Files one consolidated return.",
  },
  {
    key: "hasEmployees",
    label: "Has employees",
    help: "Enables the 1601-C compensation return.",
  },
];

const inputClass =
  "w-full border border-ink-line rounded-md px-3 py-2 text-sm text-ink bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-primary-500";

// `value` is the stored (possibly partial) profile. The controls show the
// effective profile (getWhtProfile — e.g. hasEmployees falls back to the
// compensation obligation), but onChange only adds the field the user changed,
// so untouched fields stay unset and keep their read-time fallback.
const WhtProfileFields = ({ value, taxObligations, onChange, disabled = false }) => {
  const stored = value || {};
  const profile = getWhtProfile({ whtProfile: stored, taxObligations });
  const set = (key, v) => onChange({ ...stored, [key]: v });

  return (
    <section aria-labelledby="wht-profile-heading" className="border-t border-ink-line pt-6 mt-6">
      <h2 id="wht-profile-heading" className="text-lg font-medium text-ink">
        Withholding tax profile
      </h2>
      <p className="text-sm text-ink-body mt-1 mb-4">
        Drives which withholding returns and ATCs apply to this client.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="wht-taxpayer-size" className="block text-sm font-medium text-ink mb-2">
            Taxpayer size
          </label>
          <select
            id="wht-taxpayer-size"
            value={profile.taxpayerSize ?? ""}
            onChange={(e) => set("taxpayerSize", e.target.value || null)}
            className={inputClass}
            disabled={disabled}
          >
            <option value="">Not yet classified</option>
            {TAXPAYER_SIZES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <p className="text-xs text-ink-body mt-1">
            RR 8-2024, by gross sales: micro below ₱3M · small ₱3M to below ₱20M ·
            medium ₱20M to below ₱1B · large ₱1B and above.
          </p>
        </div>

        <div>
          <label htmlFor="wht-agent-category" className="block text-sm font-medium text-ink mb-2">
            Agent category
          </label>
          <select
            id="wht-agent-category"
            value={profile.agentCategory}
            onChange={(e) => set("agentCategory", e.target.value)}
            className={inputClass}
            disabled={disabled}
          >
            <option value="private">Private</option>
            <option value="government">Government</option>
          </select>
          <p className="text-xs text-ink-body mt-1">
            Government agents can use ATC WI/WC640 and WI/WC157.
          </p>
        </div>

        {FLAGS.map((f) => (
          <label
            key={f.key}
            className="flex items-start gap-3 border border-ink-line rounded-md p-3 bg-white cursor-pointer"
          >
            <input
              type="checkbox"
              checked={!!profile[f.key]}
              onChange={(e) => set(f.key, e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-primary-500"
              disabled={disabled}
            />
            <span>
              <span className="block text-sm font-medium text-ink">{f.label}</span>
              <span className="block text-xs text-ink-body mt-0.5">{f.help}</span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
};

export default WhtProfileFields;
